// Replace a legacy agreement (Session BBZ). For an active contract signed on old paper (the pre-Guvnor Model
// Conditions agreement, whose price review uses index figures from the National Association of Lift Makers), the
// office issues a replacement on the current ESA terms at the contract's current price. It is saved as a DRAFT
// quotation: nothing is sent from here. The office sends it from the Quotes page, the signatory accepts and signs, and
// on signature the old contract ends and the new one takes over.
//
// Mounted on the Details tab of ContractDetailPage.tsx, for an active contract.
import { useState } from "react";
import { api } from "../../lib/api";
import type { ReplacementAgreementResult } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Button } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";

const INPUT = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green";
const SMALL_BTN = "rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-strong disabled:opacity-50";

const gbp = (n: number) => `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tierLabel = (t: string) => (t === "cover" || t === "care" ? "Care" : t === "complete" ? "Complete" : "Core");

export function ReplacementAgreementSection({ contractId }: { contractId: string }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [preview, setPreview] = useState<ReplacementAgreementResult | null>(null);
  const [done, setDone] = useState<ReplacementAgreementResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(commit: boolean) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.contracts.replacementAgreement(contractId, { name: name.trim(), email: email.trim() }, !commit);
      if (commit) setDone(res);
      else setPreview(res);
    } catch (err) {
      setError(friendlyApiError(err, "Could not prepare the replacement agreement. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-2 font-heading text-lg text-ele-strong">Replacement agreement</h2>
      <p className="text-sm text-ele-body">
        Put this contract onto the current agreement (the CPI price review) at the price it pays now, for example when it was signed on the old
        Model Conditions paper. It is saved as a draft quotation for you to send; nothing goes to the customer from here.
      </p>
      {!open && !done && (
        <div className="mt-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(true)}>
            Prepare a replacement agreement
          </Button>
        </div>
      )}
      {open && !done && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <div>
            <label className="block text-xs text-ele-body" htmlFor="replacement-signatory-name">
              Signatory name
            </label>
            <input id="replacement-signatory-name" className={INPUT} value={name} onChange={(e) => { setName(e.target.value); setPreview(null); }} />
          </div>
          <div>
            <label className="block text-xs text-ele-body" htmlFor="replacement-signatory-email">
              Signatory email
            </label>
            <input id="replacement-signatory-email" className={INPUT} value={email} onChange={(e) => { setEmail(e.target.value); setPreview(null); }} />
          </div>
          <div className="sm:col-span-2">
            <button type="button" className={SMALL_BTN} disabled={busy || !name.trim() || !email.trim()} onClick={() => void run(false)}>
              {busy && !preview ? "Checking..." : "Preview"}
            </button>
          </div>
        </div>
      )}
      {preview && !done && (
        <div className="mt-3 rounded-ele border border-ele-purple-accent bg-ele-bg p-3 text-sm text-ele-body">
          <p className="font-semibold text-ele-strong">
            {tierLabel(preview.plan.tier)} cover, {gbp(preview.plan.annualValue)} a year ex VAT, {preview.plan.lifts} lift{preview.plan.lifts === 1 ? "" : "s"}
          </p>
          <p className="mt-1">This is the contract&rsquo;s current price, carried as it is. A fresh quote would say {gbp(preview.plan.enginePriceForThisTier)}; that figure is not used.</p>
          <p className="mt-1">
            The accept link, the agreement, the welcome pack and the portal login go to {preview.plan.signatory.name} ({preview.plan.signatory.email}) only.
          </p>
          <p className="mt-1">{preview.plan.newReviewNote}</p>
          <p className="mt-1">{preview.plan.oldPaperNote}</p>
          <div className="mt-2">
            <ConfirmButton
              onConfirm={() => void run(true)}
              label="Save as a draft quotation"
              confirmLabel="Save the replacement as a draft?"
              busy={busy}
              busyLabel="Saving..."
              className={SMALL_BTN}
            />
          </div>
        </div>
      )}
      {done && (
        <p role="status" className="mt-3 text-sm text-ele-green">
          Saved as draft quotation {done.quoteRef}. {done.next}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}
